import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Sparkles } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MutualConnections — LinkedIn-style mutual trade-partners callout.
 *
 * Shows "X people you've traded with have also traded with {ownerName}"
 * plus stacked avatar previews. Falls back gracefully when there are no
 * mutuals OR when the current user has never completed a trade.
 *
 * Props:
 *   - userId: string (the target user — usually a listing's owner)
 *   - ownerName: string (display name of the target, for copy)
 *   - compact?: boolean (smaller variant for inline use)
 */
export default function MutualConnections({ userId, ownerName, compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !userId || user.user_id === userId) {
      setData(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api.get(`/social/mutual/${userId}`)
      .then(({ data: d }) => { if (active) setData(d); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user, userId]);

  if (!user || !userId || user.user_id === userId) return null;
  if (loading) return null;

  const total = data?.total || 0;
  const direct = data?.your_trades_with_target || 0;
  const preview = data?.preview || [];

  // Show nothing if there's no signal at all
  if (total === 0 && direct === 0) return null;

  // === DIRECT TRADE — strongest trust signal ===
  if (direct > 0) {
    return (
      <div className={`rounded-md ${compact ? "p-3" : "p-3.5"} bg-[#E0EDFA] border border-[#BFDBFE] flex items-center gap-2.5`} data-testid="mutual-direct">
        <div className="w-7 h-7 rounded-full bg-[#0654BA] text-white flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
        </div>
        <div className="text-[12px] md:text-[13px] leading-snug text-slate-800">
          <span className="font-bold">You&apos;ve already traded</span> with {ownerName}
          <span className="text-slate-600"> · {direct} successful {direct === 1 ? "trade" : "trades"}</span>
        </div>
      </div>
    );
  }

  // === MUTUAL CONNECTIONS — LinkedIn-style ===
  return (
    <div className={`rounded-md border border-slate-200 bg-slate-50 ${compact ? "p-3" : "p-3.5"}`} data-testid="mutual-connections">
      <div className="flex items-center gap-2.5">
        {/* Stacked avatars */}
        <div className="flex -space-x-2 flex-shrink-0">
          {preview.slice(0, 3).map((u, i) => (
            <Link
              key={u.user_id}
              to={`/profile/${u.user_id}`}
              className="block relative"
              title={`${u.name}${u.trades_with_target > 1 ? ` · ${u.trades_with_target} trades` : ""}`}
              style={{ zIndex: 10 - i }}
              data-testid={`mutual-avatar-${u.user_id}`}
            >
              {u.picture ? (
                <img src={imgSrc(u.picture)} alt={u.name} className="w-7 h-7 rounded-full object-cover border-2 border-slate-50 hover:border-[#0654BA] transition-colors" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-slate-50 flex items-center justify-center text-[10px] font-bold text-slate-600">
                  {u.name?.[0]?.toUpperCase()}
                </div>
              )}
            </Link>
          ))}
          {total > 3 && (
            <div className="w-7 h-7 rounded-full bg-slate-100 border-2 border-slate-50 flex items-center justify-center text-[10px] font-bold text-slate-600">
              +{total - 3}
            </div>
          )}
        </div>

        {/* Copy */}
        <div className="text-[12px] md:text-[13px] leading-snug text-slate-700 flex-1 min-w-0">
          <span className="font-bold text-slate-900">
            {preview[0]?.name}
            {total > 1 && <span className="font-normal text-slate-500"> and {total - 1} other{total - 1 === 1 ? "" : "s"} you know</span>}
          </span>
          <span className="text-slate-500"> {total === 1 ? "has" : "have"} traded with {ownerName}</span>
        </div>
      </div>
    </div>
  );
}
